/**
 * Single source of truth for every string on the page.
 * Sections render this data — copy changes never require touching a component.
 */

import type { IconName } from '@/components/Icon';

export interface NavLink {
  readonly id: string;
  readonly label: string;
}

export interface Service {
  readonly id: string;
  readonly icon: IconName;
  readonly title: string;
  readonly tagline: string;
  readonly description: string;
  readonly deliverables: readonly string[];
}

export interface ProcessStep {
  readonly phase: string;
  readonly title: string;
  readonly description: string;
  readonly duration: string;
}

export interface CaseStudy {
  readonly id: string;
  readonly sector: string;
  readonly title: string;
  readonly summary: string;
  readonly engagement: readonly string[];
  readonly metric: { readonly value: string; readonly label: string };
}

export interface CapabilityGroup {
  readonly title: string;
  readonly items: readonly string[];
}

export interface Testimonial {
  readonly quote: string;
  readonly author: string;
  readonly role: string;
}

/** A headline fragment; `accent` fragments get the gradient treatment. */
export interface HeadlinePart {
  readonly text: string;
  readonly accent?: boolean;
}

export interface CodeLine {
  readonly kind: 'code' | 'comment' | 'output';
  readonly text: string;
}

export interface Hero {
  readonly eyebrow: string;
  readonly headline: readonly HeadlinePart[];
  readonly lead: string;
  readonly primaryCta: { readonly label: string; readonly href: string };
  readonly secondaryCta: { readonly label: string; readonly href: string };
  readonly stats: readonly { readonly value: string; readonly label: string }[];
  readonly panel: { readonly file: string; readonly lines: readonly CodeLine[] };
}

export const brand = {
  name: 'qvantum-os',
  tagline: 'Software design, build & consulting',
  email: 'hello@qvantum-os.com',
  location: 'Athens · Remote across the EU',
  established: 2019,
} as const;

export const navLinks: readonly NavLink[] = [
  { id: 'services', label: 'Services' },
  { id: 'process', label: 'Process' },
  { id: 'work', label: 'Work' },
  { id: 'capabilities', label: 'Capabilities' },
  { id: 'contact', label: 'Contact' },
];

export const hero: Hero = {
  eyebrow: 'design · build · consult',
  headline: [
    { text: 'Software that behaves ' },
    { text: 'like a system', accent: true },
    { text: ', not a pile of features.' },
  ],
  lead: 'qvantum-os is a small senior studio. We shape the product, ship the production system behind it, and stay close enough to be accountable for how it runs.',
  primaryCta: { label: 'Start a project', href: '#contact' },
  secondaryCta: { label: 'See how we work', href: '#process' },
  stats: [
    { value: '40+', label: 'products shipped' },
    { value: '7 yrs', label: 'median team seniority' },
    { value: '3 wks', label: 'to first release' },
  ],
  panel: {
    file: 'engagement.ts',
    lines: [
      { kind: 'comment', text: '// how an engagement is shaped' },
      { kind: 'code', text: 'export const engagement = defineEngagement({' },
      { kind: 'code', text: "  discovery: '1 week, fixed price'," },
      { kind: 'code', text: "  team: ['product design', 'engineering', 'architecture']," },
      { kind: 'code', text: '  cadence: weekly({ demo: true, decisions: true }),' },
      { kind: 'code', text: '  handover: { docs: true, tests: true, runbook: true },' },
      { kind: 'code', text: '});' },
      { kind: 'output', text: 'scoped, staffed, shipping' },
    ],
  },
};

export const services: readonly Service[] = [
  {
    id: 'design',
    icon: 'compass',
    title: 'Design',
    tagline: 'Decide what to build, and why.',
    description:
      'Product discovery, interface design and a design system your engineers can actually implement. We work in the browser early, so decisions are made against something real.',
    deliverables: [
      'Discovery & product framing',
      'UX flows and interaction design',
      'Design system + component library',
      'Clickable prototypes',
      'Accessibility baseline (WCAG 2.2 AA)',
    ],
  },
  {
    id: 'build',
    icon: 'layers',
    title: 'Build',
    tagline: 'Production systems, not demos.',
    description:
      'Full-stack delivery with the boring parts included: typed contracts, tests, CI/CD, observability and a deployment story from day one. You get a codebase your team can own.',
    deliverables: [
      'Web apps and design-system front ends',
      'APIs, data models and integrations',
      'Cloud infrastructure as code',
      'Test suites and CI/CD pipelines',
      'Observability, alerting, runbooks',
    ],
  },
  {
    id: 'consult',
    icon: 'radar',
    title: 'Consult',
    tagline: 'A second opinion with skin in the game.',
    description:
      'Architecture reviews, delivery diagnostics and hands-on technical leadership. We give you a written assessment with trade-offs and a sequenced plan — then help you execute it.',
    deliverables: [
      'Architecture and code audits',
      'Cloud cost and performance reviews',
      'Delivery and team diagnostics',
      'Legacy modernisation roadmaps',
      'Fractional CTO / tech lead',
    ],
  },
];

export const process: readonly ProcessStep[] = [
  {
    phase: '01',
    title: 'Discover',
    description:
      'A fixed-price week. We interview stakeholders, read the existing code, map constraints, and leave you with a scope, a risk list and an estimate you can act on.',
    duration: '1 week',
  },
  {
    phase: '02',
    title: 'Shape',
    description:
      'Flows, interface design and the architecture sketch, reviewed together. Nothing enters a sprint before we agree on what "done" looks like.',
    duration: '1–3 weeks',
  },
  {
    phase: '03',
    title: 'Build',
    description:
      'Weekly demos on a real environment. Trunk-based, tested, reviewed. You see progress in a URL, not in a status report.',
    duration: 'iterative',
  },
  {
    phase: '04',
    title: 'Harden',
    description:
      'Load and failure testing, security review, accessibility pass, observability wired up. We fix what we find before launch, not after it.',
    duration: '1–2 weeks',
  },
  {
    phase: '05',
    title: 'Hand over',
    description:
      'Documentation, runbooks and pairing sessions until your team is comfortable. We stay available on retainer, but never as a dependency.',
    duration: 'ongoing',
  },
];

export const caseStudies: readonly CaseStudy[] = [
  {
    id: 'payments',
    sector: 'Fintech',
    title: 'Payment orchestration platform',
    summary:
      'Replaced a monolithic gateway integration with a routing layer across five providers, with idempotent retries and end-to-end transaction traceability.',
    engagement: ['Architecture', 'Build', 'Observability'],
    metric: { value: '−63%', label: 'failed transactions' },
  },
  {
    id: 'logistics',
    sector: 'Logistics',
    title: 'Fleet operations console',
    summary:
      'Designed and shipped a real-time console for 1,200 vehicles: live telemetry, dispatch, and an offline-tolerant driver app used across three countries.',
    engagement: ['Design', 'Build'],
    metric: { value: '4.6×', label: 'faster dispatch' },
  },
  {
    id: 'health',
    sector: 'Health tech',
    title: 'Clinical data platform audit',
    summary:
      'A six-week audit of a scaling platform: data model, tenancy and release process. Delivered a sequenced modernisation plan the team executed in two quarters.',
    engagement: ['Consult', 'Roadmap'],
    metric: { value: '−41%', label: 'cloud spend' },
  },
];

export const capabilities: readonly CapabilityGroup[] = [
  {
    title: 'Front end',
    items: ['TypeScript', 'React', 'Next.js', 'Design systems', 'Web performance'],
  },
  {
    title: 'Back end',
    items: ['Node.js', 'Go', 'Python', 'PostgreSQL', 'Event-driven APIs'],
  },
  {
    title: 'Platform',
    items: ['AWS', 'Azure', 'Terraform', 'Kubernetes', 'GitHub Actions'],
  },
  {
    title: 'Practice',
    items: ['Domain-driven design', 'Test strategy', 'Observability', 'Accessibility', 'Threat modelling'],
  },
];

export interface Principle {
  readonly title: string;
  readonly text: string;
}

export const principles: readonly Principle[] = [
  {
    title: 'Senior-only teams',
    text: 'Everyone on your engagement has shipped and operated systems in production. No pyramid staffing, no juniors billed as leads.',
  },
  {
    title: 'Decisions in writing',
    text: 'Architecture choices land in short decision records with the trade-offs stated, so the reasoning outlives the people who made it.',
  },
  {
    title: 'Built for handover',
    text: 'We measure success by how well your team runs the thing without us. Documentation and tests are part of the work, not an extra.',
  },
];

export const testimonials: readonly Testimonial[] = [
  {
    quote:
      'They spent the first week telling us which half of our roadmap to delete. That conversation was worth more than the code — and the code was excellent.',
    author: 'Head of Product',
    role: 'European payments scale-up',
  },
  {
    quote:
      'The handover was the most impressive part. Runbooks, tests, and two weeks of pairing. Our team owned it completely by month three.',
    author: 'VP Engineering',
    role: 'Logistics operator, 400 staff',
  },
];

export const contact = {
  heading: 'Tell us what you are building.',
  lead: 'A short description of the problem is enough to start. We reply within two working days, and the first call is a conversation, not a pitch.',
  points: [
    'Discovery weeks are fixed price and standalone — no obligation to continue.',
    'We take on three to four engagements at a time, so we say no often.',
    'Comfortable under NDA, DPA and existing procurement.',
  ],
  budgets: ['< €25k', '€25k – €75k', '€75k – €200k', '€200k +', 'Not sure yet'],
  interests: ['Design', 'Build', 'Consult'],
} as const;
